import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, TextField, Button, Grid, CircularProgress, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton
} from '@mui/material';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, where, writeBatch, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Takkevariheader from './Takkevariheader';
import moment from 'moment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';


const db = getFirestore();

function Amountgiven() {
    const [customers, setCustomers] = useState([]);
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate(); // <-- initialize navigate


    const [loanDetails, setLoanDetails] = useState({
        customerId: '',
        date: new Date().toISOString().slice(0, 10),
        amount: '',
        ghetleliRakkam: '',
        jamaRakkam: '',
        jamaDinank: '',
        interestRate: '',
        tenure: 1,
        commission: "",
        place: "",
        interestTakenDate: new Date().toISOString().slice(0, 10),
    });
    const [editingLoan, setEditingLoan] = useState(null);
    const [monthlyInterestAmount, setMonthlyInterestAmount] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Fetch customers and loans
    const fetchLoansAndCustomers = async () => {
        setLoading(true);
        try {
            const customersSnapshot = await getDocs(collection(db, 'takkevaricusts'));
            const customerList = customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCustomers(customerList);

            const customersMap = customerList.reduce((acc, customer) => {
                acc[customer.id] = customer.name;
                return acc;
            }, {});

            const loansSnapshot = await getDocs(collection(db, 'takkevariloans'));
            const loanList = loansSnapshot.docs.map(doc => {
                const data = doc.data();
                return { id: doc.id, ...data, loandate: data.loandate.toDate() };
            });

            const loansWithCustomerNames = loanList.map(loan => ({
                ...loan,
                customerName: customersMap[loan.customerId] || 'N/A',
            }));

            const activeLoans = loansWithCustomerNames
                .filter(loan => loan.status === 'active')
                .sort((a, b) => a.loandate.getTime() - b.loandate.getTime()); // ascending
            setLoans(activeLoans);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLoansAndCustomers();
    }, []);

    // Calculate monthly interest automatically
    // Inside useEffect for monthly interest calculation
    useEffect(() => {
        const principal = parseFloat(loanDetails.amount);
        const rate = parseFloat(loanDetails.interestRate);

        if (!isNaN(principal) && principal > 0 && !isNaN(rate) && rate > 0) {
            const interest = (principal * rate) / 100;
            setMonthlyInterestAmount(interest.toFixed(2));

            // Only set ghetleliRakkam if NOT editing
            if (!editingLoan) {
                setLoanDetails(prev => ({ ...prev, }));
            }
        } else {
            setMonthlyInterestAmount('');
            if (!editingLoan) setLoanDetails(prev => ({ ...prev, ghetleliRakkam: '' }));
        }
    }, [loanDetails.amount, loanDetails.interestRate, editingLoan]);


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLoanDetails(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        let formErrors = {};
        if (!loanDetails.customerId) formErrors.customerId = 'Customer is required.';
        if (!loanDetails.amount || isNaN(loanDetails.amount) || parseFloat(loanDetails.amount) <= 0) {
            formErrors.amount = 'Valid amount is required.';
        }
        if (!loanDetails.interestRate || isNaN(loanDetails.interestRate) || parseFloat(loanDetails.interestRate) <= 0) {
            formErrors.interestRate = 'Valid interest rate is required.';
        }
        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleCreateOrUpdateLoan = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const loanData = {
                customerId: loanDetails.customerId,
                loandate: new Date(loanDetails.date),
                amount: parseFloat(loanDetails.amount),
                ghetleliRakkam: parseFloat(loanDetails.ghetleliRakkam),
                jamaRakkam: loanDetails.jamaRakkam ? parseFloat(loanDetails.jamaRakkam) : 0,
                jamaDinank: loanDetails.jamaDinank ? new Date(loanDetails.jamaDinank) : null,
                tenure: loanDetails.tenure || 1,
                commission: loanDetails.commission || '',
                place: loanDetails.place || '',
                interestRate: parseFloat(loanDetails.interestRate),
                interestTakenDate: loanDetails.interestTakenDate ? new Date(loanDetails.interestTakenDate) : null,
                status: 'active',
                createdAt: new Date(),
                monthlyInterestAmount
            };

            if (editingLoan) {
                await updateDoc(doc(db, 'takkevariloans', editingLoan.id), loanData);
                toast.success("Loan updated successfully!");
                setEditingLoan(null);
            } else {
                await setDoc(doc(collection(db, 'takkevariloans')), loanData);
                toast.success("Loan created successfully!");
            }

            setLoanDetails({
                customerId: '',
                date: new Date().toISOString().slice(0, 10),
                amount: '',
                ghetleliRakkam: '',
                jamaRakkam: '',
                jamaDinank: '',
                interestRate: '',
                tenure: 1,
                commission: "",
                place: "",
                interestTakenDate: new Date().toISOString().slice(0, 10),
            });
            fetchLoansAndCustomers();
        } catch (error) {
            console.error(error);
            toast.error("Failed to save loan.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = (loan) => {
        setEditingLoan(loan);
        setLoanDetails({
            customerId: loan.customerId || '',
            date: loan.loandate ? moment(loan.loandate.toDate ? loan.loandate.toDate() : loan.loandate).format('YYYY-MM-DD') : '',
            amount: loan.amount || '',
            ghetleliRakkam: loan.ghetleliRakkam || '',
            jamaRakkam: loan.jamaRakkam || '',
            jamaDinank: loan.jamaDinank ? moment(loan.jamaDinank.toDate ? loan.jamaDinank.toDate() : loan.jamaDinank).format('YYYY-MM-DD') : '',
            interestRate: loan.interestRate || '',
            tenure: loan.tenure || 1,
            commission: loan.commission || '',
            place: loan.place || '',
            interestTakenDate: loan.interestTakenDate ? moment(loan.interestTakenDate.toDate ? loan.interestTakenDate.toDate() : loan.interestTakenDate).format('YYYY-MM-DD') : '',
        });
    };


    const handleDeleteClick = async (loan) => {
        if (!window.confirm('Are you sure you want to delete this loan?')) return;
        setIsSubmitting(true);
        try {
            const interestsQuery = query(collection(db, 'takkevariintrests'), where('takkevariloansId', '==', loan.id));
            const interestsSnapshot = await getDocs(interestsQuery);
            const batch = writeBatch(db);
            interestsSnapshot.docs.forEach(doc => batch.delete(doc.ref));
            await batch.commit();
            await deleteDoc(doc(db, 'takkevariloans', loan.id));
            toast.success("Loan deleted successfully!");
            fetchLoansAndCustomers();
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete loan.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredLoans = loans.filter(loan =>
        loan.customerName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const displayedLoans = filteredLoans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;

    const handleViewClick = (loan) => {
        // Example: navigate to a details page
        navigate(`/viewintresthistory/${loan.id}`);
        // Or open a modal with interest/payment history
    };


    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Takkevariheader />

            {/* Loan Form */}
            <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" gutterBottom>{editingLoan ? 'Update Loan' : 'Add Loan'}</Typography>
                <form onSubmit={handleCreateOrUpdateLoan}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' label="घेतलेली रक्कम" name="ghetleliRakkam" value={loanDetails.ghetleliRakkam} onChange={handleInputChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField select fullWidth size='small' label="खातेदार निवडा" name="customerId" value={loanDetails.customerId} onChange={handleInputChange} disabled={!!editingLoan} error={!!errors.customerId} helperText={errors.customerId}>
                                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' type="date" label="कर्ज तारीख" name="date" value={loanDetails.date} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' label="दिलेली रक्कम (₹)" name="amount" value={loanDetails.amount} onChange={handleInputChange} error={!!errors.amount} helperText={errors.amount} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' type="number" label="टक्केवारी (%)" name="interestRate" value={loanDetails.interestRate} onChange={handleInputChange} error={!!errors.interestRate} helperText={errors.interestRate} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' type="date" label="व्याज घ्यायची तारीख" name="interestTakenDate" value={loanDetails.interestTakenDate} onChange={handleInputChange} InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' label="व्याज घ्यायची रक्कम" value={monthlyInterestAmount} InputProps={{ readOnly: true }} sx={{ backgroundColor: '#e0f7fa' }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' label="कमिशन" name="commission" value={loanDetails.commission} onChange={handleInputChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size='small' label="ठिकाण" name="place" value={loanDetails.place} onChange={handleInputChange} />
                        </Grid>
                        <Grid item xs={12}>
                            {editingLoan && <Button variant="outlined" color="error" onClick={() => { setEditingLoan(null); setLoanDetails({ customerId: '', date: new Date().toISOString().slice(0, 10), amount: '', ghetleliRakkam: '', jamaRakkam: '', jamaDinank: '', interestRate: '', tenure: 1, commission: '', place: '', interestTakenDate: new Date().toISOString().slice(0, 10) }); }}>Cancel Edit</Button>}
                            <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} sx={{ ml: editingLoan ? 2 : 0 }}>
                                {isSubmitting ? <CircularProgress size={24} /> : editingLoan ? 'Update Loan' : 'Create Loan'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {/* Loan Table */}
            <Paper sx={{ p: 2, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <TextField fullWidth size='small' placeholder="Search by customer" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ mb: 2 }} />
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>क्र. नं.</TableCell>
                                <TableCell>खातेदाराचे नाव</TableCell>
                                <TableCell>रक्कम</TableCell>
                                <TableCell>घेतलेली रक्कम</TableCell>
                                <TableCell>व्याज दर (%)</TableCell>
                                <TableCell>महिन्याचे व्याज</TableCell>
                                <TableCell>कर्ज तारीख</TableCell>
                                <TableCell>क्रिया</TableCell>
                            </TableRow>

                        </TableHead>
                        <TableBody>
                            {displayedLoans.map((loan, index) => (
                                <TableRow key={loan.id}>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell> {/* Sr No */}
                                    <TableCell>{loan.customerName}</TableCell>
                                    <TableCell>{loan.amount}</TableCell>
                                    <TableCell>{loan.ghetleliRakkam}</TableCell>
                                    <TableCell>{loan.interestRate}</TableCell>
                                    <TableCell>{((loan.amount * loan.interestRate) / 100).toFixed(2)}</TableCell>
                                    <TableCell>{moment(loan.loandate).format('DD-MM-YYYY')}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(loan)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(loan)}>
                                            <DeleteIcon />
                                        </IconButton>
                                        <IconButton size="small" color="info" onClick={() => handleViewClick(loan)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filteredLoans.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>
        </Box>
    );
}

export default Amountgiven;
